

// client/src/pages/invoice/UploadPage.jsx

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function UploadPage() {

  const navigate   = useNavigate();
  const inputRef   = useRef(null);
  const [files, setFiles]       = useState([]);
  const [hovering, setHovering] = useState(false);

  // ── HEADER SCROLL ──
  const [hidden, setHidden]     = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      if (y > lastY.current && y > 120) setHidden(true);
      else setHidden(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── FILE HANDLING ──
  const handleFiles = (incoming) => {
    const arr = Array.from(incoming).filter((f) =>
      f.type === "application/pdf" || f.name.endsWith(".pdf")
    );
    if (arr.length) setFiles((prev) => [...prev, ...arr]);
  };

  const handleInputChange  = (e) => handleFiles(e.target.files);
  const handleDragOver     = (e) => { e.preventDefault(); setHovering(true); };
  const handleDragLeave    = ()  => setHovering(false);
  const handleDrop         = (e) => {
    e.preventDefault();
    setHovering(false);
    handleFiles(e.dataTransfer.files);
  };
  const handleZoneClick    = ()  => inputRef.current?.click();
  const removeFile         = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const visibleFiles = files.slice(0, 4);
  const extraCount   = files.length - visibleFiles.length;

  return (
    <div style={styles.page}>

      {/* ── HEADER ── */}
      <header style={{
        ...styles.header,
        ...(scrolled ? styles.headerScrolled : {}),
        ...(hidden   ? styles.headerHidden   : {}),
      }}>
        <div style={styles.headerInner}>
          <div style={styles.brand}>
            <div style={styles.logoWrap}>📄</div>
            <div>
              <div style={styles.brandTitle}>PSBS Invoice Processor</div>
              <div style={styles.brandSubtitle}>Raw PDF Analyzer</div>
            </div>
          </div>
          <div style={styles.statusPill}>
            <span style={styles.statusDot} />
            <span style={styles.statusText}>System Online</span>
          </div>
        </div>
      </header>

      {/* ── MAIN ── */}
      <main style={styles.main}>

        {/* PAGE HEADER */}
        <div style={styles.pageHeader}>
          <div style={styles.eyebrowRow}>
            <span style={styles.eyebrowDot} />
            <span style={styles.eyebrow}>Invoice</span>
            <span style={styles.eyebrowSep}>/</span>
            <span style={styles.eyebrowMuted}>Upload</span>
          </div>
          <h1 style={styles.pageTitle}>PDF Invoice Upload</h1>
          <p style={styles.pageSubtitle}>
            Upload vendor PDF invoices to extract structured data and generate output sheets
          </p>
        </div>

        {/* CONTENT GRID */}
        <div style={styles.contentGrid}>

          {/* ── LEFT — UPLOADER CARD ── */}
          <div style={styles.card}>

            {/* Card header */}
            <div style={styles.cardHeader}>
              <div style={styles.iconWrap}>
                <span style={styles.cardIcon}>📂</span>
              </div>
              <div>
                <div style={styles.cardTitle}>Select Files</div>
                <div style={styles.cardSubtitle}>Supports PDF format · Multiple files allowed</div>
              </div>
            </div>

            <div style={styles.divider} />

            {/* ── HIDDEN INPUT — triggered by clicking the zone ── */}
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,application/pdf"
              multiple
              style={{ display: "none" }}
              onChange={handleInputChange}
            />

            {/* ── DROP ZONE ── */}
            <div
              style={{
                ...styles.dropZone,
                ...(hovering   ? styles.dropZoneHover  : {}),
                ...(files.length ? styles.dropZoneActive : {}),
              }}
              onClick={handleZoneClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div style={styles.dropBody}>

                {/* Upload cloud SVG */}
                <div style={{
                  ...styles.uploadIconCircle,
                  ...(files.length ? styles.uploadIconCircleActive : {}),
                  ...(hovering    ? styles.uploadIconCircleHover  : {}),
                }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                    stroke={files.length ? "#2563eb" : hovering ? "#6366f1" : "#9ca3af"}
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                  >
                    <polyline points="16 16 12 12 8 16" />
                    <line x1="12" y1="12" x2="12" y2="21" />
                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                  </svg>
                </div>

                {files.length > 0 ? (
                  <>
                    <div style={styles.dropLabel}>Files ready to process</div>
                    <div style={styles.dropHint}>Click or drag to add more PDFs</div>
                  </>
                ) : hovering ? (
                  <>
                    <div style={{ ...styles.dropLabel, color: "#4f46e5" }}>Release to add files</div>
                    <div style={styles.dropHint}>Drop your PDF files here</div>
                  </>
                ) : (
                  <>
                    <div style={styles.dropLabel}>Drop your PDF files here</div>
                    <div style={styles.dropHint}>or click anywhere here to browse</div>
                    <div style={styles.dropFormats}>
                      <span style={styles.formatTag}>.pdf</span>
                      <span style={styles.formatTag}>Multiple files</span>
                    </div>
                  </>
                )}

              </div>
            </div>

            {/* ── SELECTED FILES LIST ── */}
            {files.length > 0 && (
              <div style={styles.fileList}>
                <div style={styles.fileListHead}>
                  <span style={styles.fileListLabel}>Selected Files</span>
                  <span style={styles.fileCountBadge}>{files.length}</span>
                </div>

                {visibleFiles.map((f, i) => (
                  <div key={i} style={styles.fileRow}>
                    <span style={styles.fileRowIcon}>📄</span>
                    <span style={styles.fileRowName}>{f.name}</span>
                    <button
                      style={styles.fileRemoveBtn}
                      onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                      title="Remove"
                    >✕</button>
                  </div>
                ))}

                {extraCount > 0 && (
                  <div style={{ ...styles.fileRow, ...styles.fileRowMuted }}>
                    <span style={styles.fileRowIcon}>⋯</span>
                    <span style={styles.fileRowName}>
                      +{extraCount} more file{extraCount > 1 ? "s" : ""} selected
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* ── PROCESS BUTTON ── */}
            <button
              style={{
                ...styles.processBtn,
                ...(!files.length ? styles.processBtnDisabled : {}),
              }}
              disabled={!files.length}
              onClick={() =>
                navigate("/invoice/process", {
                  state: { files },
                  replace: true,
                })
              }
            >
              {files.length
                ? `Start Processing ${files.length} File${files.length > 1 ? "s" : ""} →`
                : "Start Processing →"
              }
            </button>

          </div>

          {/* ── RIGHT — INFO CARD ── */}
          <div style={styles.infoCard}>

            <div style={styles.cardHeader}>
              <div style={{ ...styles.iconWrap, backgroundColor: "#f5f3ff" }}>
                <span style={styles.cardIcon}>ℹ️</span>
              </div>
              <div>
                <div style={styles.cardTitle}>How It Works</div>
                <div style={styles.cardSubtitle}>What happens when you upload invoices</div>
              </div>
            </div>

            <div style={styles.divider} />

            <div style={styles.stepList}>
              <Step n="1" title="PDFs are parsed page by page"
                desc="Each invoice is read and relevant fields are identified automatically" />
              <Step n="2" title="Data is extracted and structured"
                desc="Vendor name, invoice number, amounts and dates are pulled from each file" />
              <Step n="3" title="Duplicates are detected"
                desc="Invoice numbers are checked against existing records to prevent double entry" />
              <Step n="4" title="Output sheet is generated"
                desc="A clean Excel sheet is created with all extracted invoice data ready for review" />
            </div>

            <div style={styles.infoNote}>
              <span style={styles.infoNoteDot} />
              <span>Multi-vendor support — all major telecom invoice formats accepted</span>
            </div>

          </div>

        </div>
      </main>
    </div>
  );
}

// ─────────────────────────────────────
// STEP SUB-COMPONENT
// ─────────────────────────────────────
function Step({ n, title, desc }) {
  return (
    <div style={styles.stepItem}>
      <div style={styles.stepNumber}>{n}</div>
      <div style={styles.stepContent}>
        <div style={styles.stepTitle}>{title}</div>
        <div style={styles.stepDesc}>{desc}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────
// STYLES
// ─────────────────────────────────────
const styles = {

  page: {
    minHeight: "100vh",
    backgroundColor: "#f9fafb",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },

  // ── HEADER ──
  header: {
    position: "fixed",
    top: 0, left: 0, right: 0,
    height: "56px",
    backgroundColor: "#0f172a",
    zIndex: 100,
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    display: "flex",
    alignItems: "center",
  },
  headerScrolled: { boxShadow: "0 1px 0 rgba(255,255,255,0.06)" },
  headerHidden:   { transform: "translateY(-100%)" },
  headerInner: {
    width: "100%", maxWidth: "1400px",
    margin: "0 auto", padding: "0 40px",
    display: "flex", alignItems: "center",
    justifyContent: "space-between", boxSizing: "border-box",
  },
  brand:         { display: "flex", alignItems: "center", gap: "12px" },
  logoWrap: {
    fontSize: "20px", width: "36px", height: "36px",
    backgroundColor: "rgba(255,255,255,0.08)",
    borderRadius: "8px",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  brandTitle:    { fontSize: "14px", fontWeight: "700", color: "#ffffff", letterSpacing: "-0.01em" },
  brandSubtitle: { fontSize: "11px", color: "rgba(255,255,255,0.45)", fontWeight: "400", marginTop: "1px" },
  statusPill: {
    display: "flex", alignItems: "center", gap: "7px",
    padding: "5px 12px",
    backgroundColor: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: "999px",
  },
  statusDot: {
    width: "7px", height: "7px", borderRadius: "50%",
    backgroundColor: "#10b981",
    boxShadow: "0 0 0 3px rgba(16,185,129,0.20)",
  },
  statusText: { fontSize: "11px", fontWeight: "600", color: "rgba(255,255,255,0.70)", letterSpacing: "0.02em" },

  // ── MAIN ──
  main: {
    paddingTop: "80px", paddingBottom: "60px",
    paddingLeft: "40px", paddingRight: "40px",
    maxWidth: "1400px", margin: "0 auto",
    boxSizing: "border-box", width: "100%",
  },

  pageHeader:   { marginBottom: "28px" },
  eyebrowRow:   { display: "flex", alignItems: "center", gap: "7px", marginBottom: "10px" },
  eyebrowDot:   { width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#6366f1", boxShadow: "0 0 0 3px rgba(99,102,241,0.15)" },
  eyebrow:      { fontSize: "11px", fontWeight: "700", color: "#4f46e5", letterSpacing: "0.07em", textTransform: "uppercase" },
  eyebrowSep:   { fontSize: "11px", color: "#d1d5db" },
  eyebrowMuted: { fontSize: "11px", fontWeight: "600", color: "#9ca3af", letterSpacing: "0.07em", textTransform: "uppercase" },
  pageTitle:    { fontSize: "18px", fontWeight: "700", color: "#111827", margin: 0, letterSpacing: "-0.01em" },
  pageSubtitle: { fontSize: "13px", color: "#6b7280", margin: "4px 0 0 0", fontWeight: "400" },

  // ── GRID ──
  contentGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",
    width: "100%",
    alignItems: "stretch",
  },

  // ── LEFT CARD ──
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    boxSizing: "border-box",
    height: "100%",
  },

  cardHeader:   { display: "flex", alignItems: "center", gap: "14px" },
  iconWrap: {
    width: "40px", height: "40px", borderRadius: "8px",
    backgroundColor: "#eff6ff",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  cardIcon:     { fontSize: "18px" },
  cardTitle:    { fontSize: "14px", fontWeight: "700", color: "#111827", letterSpacing: "-0.01em" },
  cardSubtitle: { fontSize: "12px", color: "#9ca3af", marginTop: "2px", fontWeight: "400" },
  divider:      { height: "1px", backgroundColor: "#f3f4f6", margin: "0 -24px" },

  // ── DROP ZONE ──
  dropZone: {
    border: "1.5px dashed #e5e7eb",
    borderRadius: "10px",
    backgroundColor: "#fafafa",
    minHeight: "200px",
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "border-color 0.2s, background-color 0.2s",
    userSelect: "none",
  },
  dropZoneHover: {
    borderColor: "#6366f1",
    backgroundColor: "#f5f3ff",
  },
  dropZoneActive: {
    borderColor: "#2563eb",
    backgroundColor: "#eff6ff",
  },
  dropBody: {
    display: "flex", flexDirection: "column",
    alignItems: "center", gap: "8px",
    padding: "32px 24px", textAlign: "center",
  },
  uploadIconCircle: {
    width: "52px", height: "52px", borderRadius: "12px",
    backgroundColor: "#f3f4f6",
    display: "flex", alignItems: "center", justifyContent: "center",
    marginBottom: "4px",
    transition: "background-color 0.2s",
  },
  uploadIconCircleActive: { backgroundColor: "#dbeafe" },
  uploadIconCircleHover:  { backgroundColor: "#ede9fe" },
  dropLabel:   { fontSize: "14px", fontWeight: "700", color: "#111827", letterSpacing: "-0.01em" },
  dropHint:    { fontSize: "12px", color: "#9ca3af", fontWeight: "400" },
  dropFormats: { display: "flex", gap: "6px", marginTop: "6px" },
  formatTag: {
    padding: "3px 8px",
    backgroundColor: "#f3f4f6", border: "1px solid #e5e7eb",
    borderRadius: "4px",
    fontSize: "11px", fontWeight: "600", color: "#6b7280", letterSpacing: "0.02em",
  },

  // ── FILE LIST ──
  fileList: {
    display: "flex", flexDirection: "column", gap: "8px",
    padding: "14px",
    backgroundColor: "#fafafa", border: "1px solid #f3f4f6",
    borderRadius: "8px",
  },
  fileListHead: {
    display: "flex", alignItems: "center",
    justifyContent: "space-between", marginBottom: "4px",
  },
  fileListLabel: {
    fontSize: "11px", fontWeight: "700", color: "#374151",
    letterSpacing: "0.05em", textTransform: "uppercase",
  },
  fileCountBadge: {
    fontSize: "11px", fontWeight: "700", color: "#4f46e5",
    backgroundColor: "#ede9fe", padding: "2px 8px", borderRadius: "999px",
  },
  fileRow: {
    display: "flex", alignItems: "center", gap: "8px",
    padding: "7px 10px",
    backgroundColor: "#ffffff", border: "1px solid #f3f4f6",
    borderRadius: "6px",
  },
  fileRowMuted:  { backgroundColor: "#fafafa" },
  fileRowIcon:   { fontSize: "13px", flexShrink: 0 },
  fileRowName: {
    fontSize: "12px", fontWeight: "500", color: "#374151",
    flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  fileRemoveBtn: {
    background: "none", border: "none",
    color: "#d1d5db", fontSize: "11px",
    cursor: "pointer", padding: "0 2px", lineHeight: 1, flexShrink: 0,
    transition: "color 0.15s",
  },

  // ── PROCESS BUTTON ──
  processBtn: {
    width: "100%", height: "40px",
    fontSize: "13px", fontWeight: "700",
    color: "#ffffff", backgroundColor: "#2563eb",
    border: "none", borderRadius: "8px",
    cursor: "pointer", letterSpacing: "-0.01em",
    transition: "background-color 0.15s",
  },
  processBtnDisabled: {
    backgroundColor: "#e5e7eb", color: "#9ca3af", cursor: "not-allowed",
  },

  // ── RIGHT INFO CARD ──
  infoCard: {
    backgroundColor: "#ffffff", border: "1px solid #e5e7eb",
    borderRadius: "10px", padding: "24px",
    display: "flex", flexDirection: "column", gap: "20px",
    boxSizing: "border-box", height: "100%",
  },
  stepList:    { display: "flex", flexDirection: "column", gap: "18px" },
  stepItem:    { display: "flex", gap: "14px", alignItems: "flex-start" },
  stepNumber: {
    width: "24px", height: "24px", borderRadius: "50%",
    backgroundColor: "#f3f4f6", border: "1px solid #e5e7eb",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "11px", fontWeight: "700", color: "#6b7280",
    flexShrink: 0, marginTop: "1px",
  },
  stepContent: { display: "flex", flexDirection: "column", gap: "3px" },
  stepTitle:   { fontSize: "13px", fontWeight: "600", color: "#111827" },
  stepDesc:    { fontSize: "12px", color: "#9ca3af", lineHeight: "1.5", fontWeight: "400" },
  infoNote: {
    display: "flex", alignItems: "center", gap: "7px",
    fontSize: "12px", color: "#9ca3af", fontWeight: "400",
    paddingTop: "4px", marginTop: "auto",
  },
  infoNoteDot: {
    width: "5px", height: "5px", borderRadius: "50%",
    backgroundColor: "#d1d5db", flexShrink: 0,
  },
};